<script setup lang="ts">

import TheBottomSheet from '@/components/TheBottomSheet.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import { type Component, computed, markRaw, ref } from 'vue'
import { usePlayer } from '@/composables/game/player'
import { useConfig } from '@/composables/game/config'
import MultiTapIcon from '@/components/icons/MultiTapIcon.vue'
import EnergyIcon from '@/components/icons/EnergyIcon.vue'
import CoinIcon from '@/assets/images/icons/gold-coin-icon-s3.webp'
import { formatNumberFullTruncate, formatUnshortenedNumber } from '@/helpers/utils'
import router from '@/router'
import { RouteName, routePath } from '@/routes'
import { type NotificationType, useToaster } from '@/composables/game/toasters'
import HandsWithCircle from '@/components/icons/HandsWithCircle.vue'
import LightningWithSpinner from '@/components/icons/LightningWithSpinner.vue'
import MultiTapWithCircle from '@/components/icons/MultiTapWithCircle.vue'
import FlashIconGIF from '@/components/icons/FlashIconGIF.vue'
import { T, useTranslate } from '@tolgee/vue'
import { useHapticFeedback } from '@/composables/game/haptic-feedback'
import NumberFormatTooltip from '@/components/NumberFormatTooltip.vue'

enum Boost {
  FULL_ENERGY,
  MULTITAP,
  ENERGY_LIMIT
}

type BoostAction = {
  key: string
  icon: Component,
  title: string,
  action: () => Promise<void>,
  isAvailable: boolean,
  notificationMessage?: string,
  notificationType?: NotificationType
  notificationIcon?: Component
  secondaryNotificationMessage?: string
  secondaryNotificationType?: NotificationType
  secondaryNotificationIcon?: Component
}

const props = defineProps<{
  show: boolean
  selectedBoost: Boost
}>()

const emit = defineEmits(['hide'])

const {
  player,
  upgradeTap,
  useRefillBooster,
  canUseRefillBooster,
  canUpgradeTap,
  nextRefillBoosterAvailableIn,
} = usePlayer()

const { config } = useConfig()
const { notify } = useToaster()
const { t } = useTranslate()
const { addHapticFeedback } = useHapticFeedback()

const isActionLoading = ref(false)

const actions = computed<Record<Boost, BoostAction>>(() => {
  return {
    [Boost.FULL_ENERGY]: {
      key: 'full-energy',
      icon: FlashIconGIF,
      title: t.value('boost.full_energy', 'Full energy'),
      action: async () => {
        await useRefillBooster()
      },
      isAvailable: canUseRefillBooster.value || (player.value.refillBooster.available === player.value.refillBooster.max && !nextRefillBoosterAvailableIn.value),
      notificationMessage: t.value('boost.full_energy.recharging', 'Recharging Energy...'),
      notificationType: 'warn',
      notificationIcon: markRaw(LightningWithSpinner),
      secondaryNotificationMessage: t.value('boost.full_energy.recharged', 'Energy Recharged!'),
      secondaryNotificationType: 'warn',
      secondaryNotificationIcon: markRaw(HandsWithCircle),
    },
    [Boost.MULTITAP]: {
      key: 'multitap',
      icon: MultiTapIcon,
      title: t.value('boost.boosters.multitap', 'Multitap'),
      action: async () => {
        await upgradeTap('tap')
      },
      isAvailable: canUpgradeTap('tap'),
      notificationMessage: t.value('boost.boosters.multitap.boost_is_yours', 'Boost is yours! Multitap {level} lvl', { level: config.value.tap.tap.level }),
      notificationType: 'success',
      notificationIcon: markRaw(MultiTapWithCircle),
    },
    [Boost.ENERGY_LIMIT]: {
      key: 'energy_limit',
      icon: EnergyIcon,
      title: t.value('boost.boosters.energy_limit', 'Energy limit'),
      action: async () => {
        await upgradeTap('energy')
      },
      isAvailable: canUpgradeTap('energy'),
      notificationMessage: t.value('boost.boosters.energy_limit.boost_is_yours', 'Boost is yours! Energy limit {level} lvl', { level: config.value.tap.energy.level }),
      notificationType: 'success',
    },
  }
},
)

async function doAction() {
  addHapticFeedback('medium')
  isActionLoading.value = true
  const selectedAction = actions.value[props.selectedBoost]
  await selectedAction.action()
  if (selectedAction.notificationMessage) {
    notify({
      title: selectedAction.notificationMessage,
      type: selectedAction.notificationType,
      icon: selectedAction.notificationIcon,
      duration: props.selectedBoost === Boost.FULL_ENERGY ? 1000 : undefined,
    })
  }
  setTimeout(async () => {
    emit('hide')
    isActionLoading.value = false
    addHapticFeedback()
    if (selectedAction.secondaryNotificationMessage) {
      notify({
        title: selectedAction.secondaryNotificationMessage,
        type: selectedAction.secondaryNotificationType,
        icon: selectedAction.secondaryNotificationIcon,
      })
    }
    await router.push(routePath[RouteName.HOME])
  }, 1000)

}
</script>

<template>
  <TheBottomSheet
    :show="show && !!config"
    @hide="$emit('hide')"
  >
    <div class="w-full flex flex-col items-center gap-y-8">
      <div class="flex flex-col w-full items-center text-light-white">
        <component
          :ref="actions[selectedBoost].key"
          :is="actions[selectedBoost].icon"
          class="w-[120px] h-[120px]"
        />
        <h2 class="mt-6 text-2xl font-bold -tracking-[0.48px]">
          {{ actions[selectedBoost].title.toUpperCase() }}
        </h2>
        <div
          v-if="selectedBoost === Boost.FULL_ENERGY"
          class="flex flex-col items-center gap-y-6 text-center"
        >
          <p class="font-medium text-base text-white/60">
            <T
              key-name="boost.modal.full_energy.description"
              default-value="Recharge your energy to the maximum and do another round"
            />
          </p>
          <div class="flex items-center gap-x-2">
            <img
              :src="CoinIcon"
              class="h-8"
            >
            <span class="font-bold text-[24px] leading-10 text-teal-primary -tracking-[0.5px]">
              <T
                key-name="boost.modal.full_energy.free"
                default-value="Free"
              />
            </span>
          </div>
        </div>
        <div
          v-if="selectedBoost === Boost.MULTITAP"
          class="flex flex-col items-center text-center gap-y-6"
        >
          <p class="font-medium text-base text-white/60">
            <T
              key-name="boost.modal.multitap.description"
              default-value="Increase the amount of coins you can earn per tap"
            />
          </p>
          <p class="font-medium text-base text-white/[.87]">
            {{ `+${config.tap.tap.earnPerTapDelta} coin for tap for level ${config.tap.tap.level}` }}
          </p>
          <div class="flex items-center gap-x-3">
            <NumberFormatTooltip
              :number="config.tap.tap.price"
              :formatting-type="'full'"
            >
              <div
                class="flex items-center gap-x-2"
              >
                <img
                  :src="CoinIcon"
                  class="h-8"
                >
                <span class="font-bold text-[24px] leading-10 text-teal-primary -tracking-[0.5px]">{{
                  formatNumberFullTruncate(config.tap.tap.price)
                }} </span>
              </div>
            </NumberFormatTooltip>
            <div class="w-[1px] h-[41px] bg-white/20" />
            <p class="text-white font-medium text-[24px] leading-6 -tracking-[0.48px]">
              {{ `Lvl ${config.tap.tap.level}` }}
            </p>
          </div>
        </div>
        <div
          v-if="selectedBoost === Boost.ENERGY_LIMIT"
          class="flex flex-col items-center text-center gap-y-6"
        >
          <p class="font-medium text-base text-white/60">
            <T
              key-name="boost.modal.energy_limit.description"
              default-value="Increase the amount of energy"
            />
          </p>
          <p class="font-medium text-base text-white/[.87]">
            {{ `+${config.tap.energy.limitDelta} energy points for level ${config.tap.energy.level}` }}
          </p>
          <div class="flex items-center gap-x-3">
            <div
              class="flex items-center gap-x-2"
              v-tooltip="{content: formatUnshortenedNumber(config.tap.energy.price), placement: 'bottom'}"
            >
              <img
                :src="CoinIcon"
                class="h-8"
              >
              <span class="font-bold text-[24px] leading-10 text-teal-primary -tracking-[0.5px]">{{
                formatNumberFullTruncate(config.tap.energy.price)
              }} </span>
            </div>
            <div class="w-[1px] h-[41px] bg-white/20" />
            <p class="text-white font-medium text-[24px] leading-6 -tracking-[0.48px]">
              {{ `Lvl ${config.tap.energy.level}` }}
            </p>
          </div>
        </div>
      </div>
      <BaseButton
        class="w-full"
        :disabled="!actions[selectedBoost].isAvailable && selectedBoost !== Boost.FULL_ENERGY"
        :loading="isActionLoading"
        @click="doAction"
      >
        <T
          v-if="actions[selectedBoost].isAvailable || selectedBoost === Boost.FULL_ENERGY"
          key-name="boost.modal.cta"
          default-value="Go ahead"
        />
        <T
          v-else
          key-name="boost.modal.cta_disabled"
          default-value="Insufficient funds"
        />
      </BaseButton>
    </div>
  </TheBottomSheet>
</template>

<style scoped>

</style>